/**
 * Calculate core function averages from category averages for NIST assessments
 * Aggregates safeguard domain scores (e.g., GV.OC, ID.AM) into core function scores (e.g., GV, ID)
 * 
 * @param {Object} averageScores - Object containing category averages keyed by category (e.g., 'GV.OC', 'ID.AM')
 * @returns {Object} Object containing core function averages keyed by core function code (e.g., 'GV', 'ID')
 */
function calculateCoreFunctionAverages(averageScores) {
	const coreFunctionTotals = {};
	const coreFunctionCounts = {};
	
	// console.log('[calculateCoreFunctionAverages] [FIXED VERSION] Processing with fix for 0 scores - using "in" operator check');
	// console.log('[calculateCoreFunctionAverages] Input averageScores:', averageScores);
	
	// Debug: Log all RC categories in input
	const rcCategories = Object.keys(averageScores).filter(cat => cat.startsWith('RC.'));
	// console.log('[calculateCoreFunctionAverages] RC categories in input:', rcCategories.map(cat => `${cat}=${averageScores[cat]}`));
	// console.log('[calculateCoreFunctionAverages] All categories in input (order):', Object.keys(averageScores));
	
	// Aggregate scores by core function
	let processedCategories = [];
	Object.keys(averageScores).forEach(category => {
		processedCategories.push(category);
		// Check if category matches the pattern like 'DE.AE', 'GV.OC', etc.
		const coreFunctionMatch = category.match(/^([A-Z]{2})\./);
		if (coreFunctionMatch) {
			const coreFunction = coreFunctionMatch[1]; // e.g., 'DE', 'GV'
			
			// Initialize if not already present (use 'in' operator to check for undefined, not falsy check)
			// This is important because 0 is falsy, so we can't use !coreFunctionTotals[coreFunction]
			if (!(coreFunction in coreFunctionTotals)) {
				coreFunctionTotals[coreFunction] = 0;
				coreFunctionCounts[coreFunction] = 0;
			}
			
			// Include 0 as a valid score (Not Implemented is a valid maturity state)
			// Only exclude null and undefined (which indicate Not Applicable)
			const score = averageScores[category];
			
			// Special logging for RC categories to debug the issue
			/*
			if (category.startsWith('RC.')) {
				console.log(`[calculateCoreFunctionAverages] [RC DEBUG] Processing ${category}: score=${score}, type=${typeof score}, isNaN=${isNaN(score)}, null check=${score !== null}, undefined check=${score !== undefined}, current RC total=${coreFunctionTotals[coreFunction]}, current RC count=${coreFunctionCounts[coreFunction]}`);
			} else {
				console.log(`[calculateCoreFunctionAverages] Processing ${category}: score=${score}, type=${typeof score}, isNaN=${isNaN(score)}, null check=${score !== null}, undefined check=${score !== undefined}`);
			}
			*/
			
			// Check if score is valid (including 0)
			// Explicitly allow 0 as a valid score (Not Implemented is a valid maturity state)
			// Only exclude null and undefined (which indicate Not Applicable)
			// Use Number() to handle both numeric 0 and string "0" cases
			const isValidScore = score !== null && 
			                     score !== undefined && 
			                     !isNaN(Number(score));
			
			// Extra debug for RC.CO specifically
			/*
			if (category === 'RC.CO') {
				console.log(`[calculateCoreFunctionAverages] [RC.CO DEBUG] score=${score}, isValidScore=${isValidScore}, typeof=${typeof score}, valueOf=${score !== null && score !== undefined ? score.valueOf() : 'N/A'}`);
			}
			*/
			
			if (isValidScore) {
				const oldTotal = coreFunctionTotals[coreFunction];
				const oldCount = coreFunctionCounts[coreFunction];
				// Convert score to number to handle string "0" cases
				const numericScore = typeof score === 'number' ? score : Number(score);
				coreFunctionTotals[coreFunction] += numericScore;
				coreFunctionCounts[coreFunction]++;
				const newTotal = coreFunctionTotals[coreFunction];
				const newCount = coreFunctionCounts[coreFunction];
				
				// Special logging for RC categories
				/*
				if (category.startsWith('RC.')) {
					console.log(`[calculateCoreFunctionAverages] [RC DEBUG] Added ${category} (${score}) to ${coreFunction}. Old: total=${oldTotal}, count=${oldCount} → New: total=${newTotal}, count=${newCount}`);
				} else {
					console.log(`[calculateCoreFunctionAverages] Added ${category} (${score}) to ${coreFunction}. Old: total=${oldTotal}, count=${oldCount} → New: total=${newTotal}, count=${newCount}`);
				}
				*/
			} else {
				// Extra debug for RC categories that are excluded
				/*
				if (category.startsWith('RC.')) {
					console.log(`[calculateCoreFunctionAverages] [RC DEBUG] Excluded ${category} (score=${score}, type=${typeof score}) from ${coreFunction}`);
				} else {
					console.log(`[calculateCoreFunctionAverages] Excluded ${category} (score=${score}) from ${coreFunction}`);
				}
				*/
			}
		} else {
			// console.log(`[calculateCoreFunctionAverages] Skipped ${category} (no core function match)`);
		}
	});
	
	// Debug: Log which categories were processed
	const processedRC = processedCategories.filter(cat => cat.startsWith('RC.'));
	// console.log('[calculateCoreFunctionAverages] RC categories that were processed:', processedRC);
	// console.log('[calculateCoreFunctionAverages] All categories that were processed:', processedCategories);
	
	// Debug: Log final state before calculation
	// console.log('[calculateCoreFunctionAverages] Final state before calculation:');
	// console.log('  coreFunctionTotals:', coreFunctionTotals);
	// console.log('  coreFunctionCounts:', coreFunctionCounts);
	
	// Calculate core function averages
	const coreFunctionAverages = {};
	Object.keys(coreFunctionTotals).forEach(coreFunction => {
		if (coreFunctionCounts[coreFunction] > 0) {
			const average = coreFunctionTotals[coreFunction] / coreFunctionCounts[coreFunction];
			coreFunctionAverages[coreFunction] = average * 5;
			// console.log(`[calculateCoreFunctionAverages] ${coreFunction}: total=${coreFunctionTotals[coreFunction]}, count=${coreFunctionCounts[coreFunction]}, average=${average}, scaled=${coreFunctionAverages[coreFunction]}`);
		}
	});
	
	// console.log('Core function averages:', coreFunctionAverages);
	return coreFunctionAverages;
}

